/* crm-ai-assistant.jsx — Phase 5: Real AI Assistant, LLM Gateway, CRM Copilot */
const { useState, useEffect, useMemo, useRef } = React;

const PROVIDER_COLORS = { OPENAI: '#10A37F', ANTHROPIC: '#D4711A', AZURE: '#0078D4', CUSTOM: '#6366f1' };
const PROVIDER_ICONS = { OPENAI: 'Zap', ANTHROPIC: 'Sparkles', AZURE: 'Cloud', CUSTOM: 'Settings' };

/* ── LLM Provider Card ── */
const LlmProviderCard = ({ provider, onEdit, onDelete, onSetDefault }) => (
  <div style={{ background: 'var(--nx-surface)', border: '1px solid var(--nx-border)', borderRadius: 10, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
    <div style={{ width: 38, height: 38, borderRadius: 8, background: (PROVIDER_COLORS[provider.providerType] || '#6366f1') + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <NexusIcon name={PROVIDER_ICONS[provider.providerType] || 'Cpu'} size={18} style={{ color: PROVIDER_COLORS[provider.providerType] || '#6366f1' }} />
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--nx-text)' }}>{provider.name}</span>
        {provider.isDefault && <span style={{ padding: '1px 8px', borderRadius: 20, fontSize: 10, fontWeight: 700, background: 'var(--nx-accent-tint-2)', color: 'var(--nx-accent)' }}>DEFAULT</span>}
        {!provider.isActive && <span style={{ padding: '1px 8px', borderRadius: 20, fontSize: 10, background: '#EF444418', color: '#EF4444' }}>INACTIVE</span>}
      </div>
      <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginTop: 2 }}>{provider.providerType}{provider.model ? ` · ${provider.model}` : ''}</div>
    </div>
    <div style={{ display: 'flex', gap: 6 }}>
      {!provider.isDefault && <button onClick={() => onSetDefault(provider.id)} style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid var(--nx-border)', background: 'transparent', fontSize: 11, color: 'var(--nx-text-muted)', cursor: 'pointer' }}>Set Default</button>}
      <button onClick={() => onDelete(provider.id)} style={{ padding: '4px 8px', borderRadius: 6, border: 'none', background: 'transparent', color: '#EF4444', cursor: 'pointer' }}><NexusIcon name="Trash2" size={13} /></button>
    </div>
  </div>
);

/* ── LLM Gateway Settings ── */
const LlmGatewaySettings = () => {
  const [providers, setProviders] = useState([]);
  const [usage, setUsage] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [tab, setTab] = useState('providers');
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: '', providerType: 'OPENAI', model: '', isDefault: false });
  const [loading, setLoading] = useState(false);
  const [loadingInitial, setLoadingInitial] = useState(false);
  const wsId = window.__nx_ws?.id || window.__nx_ws?.workspaceId;
  const toast = useToast?.() || {};

  useEffect(() => {
    const load = async () => {
      if (!wsId) return;
      setLoadingInitial(true);
      try {
        if (window.__nx_api?.ai?.listProviders) {
          const [p, u, c] = await Promise.all([
            window.__nx_api.ai.listProviders(wsId).catch(() => []),
            window.__nx_api.ai.getUsage(wsId).catch(() => null),
            window.__nx_api.ai.getProviderCatalog(wsId).catch(() => []),
          ]);
          setProviders(Array.isArray(p) ? p : []);
          setUsage(u);
          setCatalog(Array.isArray(c) ? c : []);
        }
      } catch (e) { toast.show?.({ title: e?.message || 'Failed to load providers', type: 'error' }); }
      finally { setLoadingInitial(false); }
    };
    load();
  }, [wsId]);

  const handleAdd = async () => {
    if (!form.name || !wsId) return;
    setLoading(true);
    try {
      const p = await window.__nx_api?.ai?.createProvider(wsId, form);
      if (p) { setProviders(prev => [p, ...prev]); setAdding(false); setForm({ name: '', providerType: 'OPENAI', model: '', isDefault: false }); }
    } catch (e) { toast.show?.({ title: 'Failed to add provider', type: 'error' }); }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    if (!wsId) return;
    try { await window.__nx_api?.ai?.deleteProvider(wsId, id); setProviders(prev => prev.filter(p => p.id !== id)); }
    catch (e) { toast.show?.({ title: e?.message || 'Failed to delete provider', type: 'error' }); }
  };

  const handleSetDefault = async (id) => {
    if (!wsId) return;
    try {
      await window.__nx_api?.ai?.updateProvider(wsId, id, { isDefault: true });
      setProviders(prev => prev.map(p => ({ ...p, isDefault: p.id === id })));
    } catch (e) { toast.show?.({ title: e?.message || 'Failed to set default', type: 'error' }); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', gap: 8, borderBottom: '1px solid var(--nx-border)', paddingBottom: 0 }}>
        {['providers', 'usage'].map(t => (
          <button key={t} onClick={() => setTab(t)} style={{ padding: '8px 16px', border: 'none', background: 'transparent', fontSize: 13, fontWeight: tab === t ? 700 : 400, color: tab === t ? 'var(--nx-accent)' : 'var(--nx-text-muted)', borderBottom: tab === t ? '2px solid var(--nx-accent)' : '2px solid transparent', cursor: 'pointer', textTransform: 'capitalize' }}>{t}</button>
        ))}
      </div>

      {tab === 'providers' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button onClick={() => setAdding(true)} style={{ padding: '7px 14px', borderRadius: 8, border: 'none', background: 'var(--nx-accent)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
              <NexusIcon name="Plus" size={13} /> Add Provider
            </button>
          </div>
          {adding && (
            <div style={{ background: 'var(--nx-surface)', border: '1px solid var(--nx-accent)', borderRadius: 10, padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div>
                  <label style={{ fontSize: 11, color: 'var(--nx-text-muted)', fontWeight: 600 }}>NAME</label>
                  <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="My OpenAI" style={{ width: '100%', marginTop: 4, padding: '7px 10px', borderRadius: 7, border: '1px solid var(--nx-border)', background: 'var(--nx-bg)', color: 'var(--nx-text)', fontSize: 13, boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ fontSize: 11, color: 'var(--nx-text-muted)', fontWeight: 600 }}>PROVIDER</label>
                  <select value={form.providerType} onChange={e => setForm(f => ({ ...f, providerType: e.target.value }))} style={{ width: '100%', marginTop: 4, padding: '7px 10px', borderRadius: 7, border: '1px solid var(--nx-border)', background: 'var(--nx-bg)', color: 'var(--nx-text)', fontSize: 13 }}>
                    {['OPENAI', 'ANTHROPIC', 'AZURE', 'CUSTOM'].map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label style={{ fontSize: 11, color: 'var(--nx-text-muted)', fontWeight: 600 }}>MODEL</label>
                <input value={form.model} onChange={e => setForm(f => ({ ...f, model: e.target.value }))} placeholder="e.g. gpt-4o or claude-sonnet-5" style={{ width: '100%', marginTop: 4, padding: '7px 10px', borderRadius: 7, border: '1px solid var(--nx-border)', background: 'var(--nx-bg)', color: 'var(--nx-text)', fontSize: 13, boxSizing: 'border-box' }} />
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button onClick={() => setAdding(false)} style={{ padding: '6px 14px', borderRadius: 7, border: '1px solid var(--nx-border)', background: 'transparent', fontSize: 12, color: 'var(--nx-text-muted)', cursor: 'pointer' }}>Cancel</button>
                <button onClick={handleAdd} disabled={loading} style={{ padding: '6px 14px', borderRadius: 7, border: 'none', background: 'var(--nx-accent)', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Save Provider</button>
              </div>
            </div>
          )}
          {loadingInitial && (
            <div style={{ textAlign: 'center', padding: '32px 16px', color: 'var(--nx-text-muted)', fontSize: 13 }}>
              <NexusIcon name="Loader" size={20} style={{ marginBottom: 8, opacity: 0.5 }} />
              <div>Loading providers…</div>
            </div>
          )}
          {!loadingInitial && providers.length === 0 && !adding && (
            <div style={{ textAlign: 'center', padding: '32px 16px', color: 'var(--nx-text-muted)', fontSize: 13 }}>
              <NexusIcon name="Cpu" size={28} style={{ marginBottom: 8, opacity: 0.4 }} />
              <div>No LLM providers configured. Add one to enable AI features.</div>
            </div>
          )}
          {providers.map(p => <LlmProviderCard key={p.id} provider={p} onDelete={handleDelete} onSetDefault={handleSetDefault} />)}
        </div>
      )}

      {tab === 'usage' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {usage ? (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
                {[{ label: 'Total Requests', value: usage.total ?? 0 }].map(k => (
                  <div key={k.label} style={{ background: 'var(--nx-surface)', border: '1px solid var(--nx-border)', borderRadius: 8, padding: '12px 14px' }}>
                    <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', marginBottom: 4 }}>{k.label}</div>
                    <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--nx-text)' }}>{k.value}</div>
                  </div>
                ))}
              </div>
              {(usage.byFeature || []).length > 0 && (
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--nx-text-muted)', marginBottom: 8 }}>BY FEATURE</div>
                  {usage.byFeature.map((f, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--nx-border)', fontSize: 13 }}>
                      <span style={{ color: 'var(--nx-text)' }}>{f.feature}</span>
                      <span style={{ color: 'var(--nx-text-muted)' }}>{f._count} requests</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: 24, color: 'var(--nx-text-muted)', fontSize: 13 }}>No usage data yet.</div>
          )}
        </div>
      )}
    </div>
  );
};

/* ── Copilot Chat ── */
const CopilotChat = ({ onClose }) => {
  const STORAGE_KEY = 'crm_copilot_history';
  const [messages, setMessages] = useState(() => { try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'); } catch { return []; } });
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [context, setContext] = useState('general');
  const bottomRef = useRef(null);
  const wsId = window.__nx_ws?.id || window.__nx_ws?.workspaceId;
  const store = useStore?.();

  const SUGGESTED = ['Show stale deals', 'Forecast this quarter', 'Draft follow-up email', "What needs attention?", 'Summarize my pipeline'];

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(messages.slice(-50))); } catch { /* QuotaExceededError — storage full, continue without persisting */ }
  }, [messages]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const send = async (query) => {
    if (!query.trim() || loading) return;
    const userMsg = { role: 'user', content: query, ts: Date.now() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);
    try {
      let answer;
      if (window.__nx_api?.ai?.copilot && wsId) {
        const ctxData = context === 'deals' ? { deals: (store?.deals || []).slice(0, 5) } : {};
        const r = await window.__nx_api.ai.copilot(wsId, { query, context: ctxData });
        answer = r?.answer ?? 'No response from AI.';
      } else {
        answer = _heuristic(query, store);
      }
      setMessages(prev => [...prev, { role: 'assistant', content: answer, ts: Date.now() }]);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I encountered an error. Please try again.', ts: Date.now(), error: true }]);
    }
    setLoading(false);
  };

  const _heuristic = (q, st) => {
    const lq = q.toLowerCase();
    if (lq.includes('deal') || lq.includes('pipeline')) {
      const deals = st?.deals || [];
      return `You have ${deals.length} deals in your pipeline. ${deals.filter(d => d.value > 10000).length} are high-value (>$10k). Consider following up on deals that haven't had activity in 7+ days.`;
    }
    if (lq.includes('forecast')) return 'Based on current pipeline, weighted forecast shows strong Q-end performance. Focus on deals in Proposal and Negotiation stages for highest impact.';
    if (lq.includes('task') || lq.includes('todo')) return `I see tasks in your workspace. Prioritize overdue items linked to active deals. Would you like me to create a follow-up task?`;
    return `I'm your CRM Copilot. I can help you analyze deals, draft emails, suggest follow-ups, and surface insights from your pipeline data. What would you like to know?`;
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 500 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid var(--nx-border)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ width: 28, height: 28, borderRadius: 8, background: 'linear-gradient(135deg, var(--nx-accent), #8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <NexusIcon name="Sparkles" size={14} style={{ color: '#fff' }} />
          </div>
          <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--nx-text)' }}>CRM Copilot</span>
          <span className="nx-ai-compact-badge">{(store?.branding?.brandName || 'AI') + ' AI'}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <select value={context} onChange={e => setContext(e.target.value)} style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid var(--nx-border)', background: 'var(--nx-bg)', color: 'var(--nx-text)', fontSize: 12 }}>
            <option value="general">General</option>
            <option value="deals">Deals</option>
            <option value="contacts">Contacts</option>
          </select>
          {onClose && <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: 'var(--nx-text-muted)', cursor: 'pointer', padding: 4 }}><NexusIcon name="X" size={16} /></button>}
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {messages.length === 0 && (
          <div style={{ textAlign: 'center', padding: '32px 16px' }}>
            <div style={{ width: 56, height: 56, borderRadius: 16, background: 'linear-gradient(135deg, #6366f118, #8b5cf618)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
              <NexusIcon name="Sparkles" size={24} style={{ color: 'var(--nx-accent)' }} />
            </div>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--nx-text)', marginBottom: 6 }}>CRM Copilot</div>
            <div style={{ fontSize: 13, color: 'var(--nx-text-muted)' }}>Ask me anything about your pipeline, contacts, or next best actions.</div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} style={{ display: 'flex', flexDirection: m.role === 'user' ? 'row-reverse' : 'row', gap: 8, alignItems: 'flex-start' }}>
            <div style={{ width: 26, height: 26, borderRadius: 6, background: m.role === 'user' ? 'var(--nx-accent)' : 'var(--nx-surface)', border: '1px solid var(--nx-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <NexusIcon name={m.role === 'user' ? 'User' : 'Sparkles'} size={12} style={{ color: m.role === 'user' ? '#fff' : 'var(--nx-accent)' }} />
            </div>
            <div style={{ maxWidth: '75%', padding: '8px 12px', borderRadius: m.role === 'user' ? '12px 4px 12px 12px' : '4px 12px 12px 12px', background: m.role === 'user' ? 'var(--nx-accent)' : 'var(--nx-surface)', border: m.role === 'assistant' ? '1px solid var(--nx-border)' : 'none', color: m.role === 'user' ? '#fff' : 'var(--nx-text)', fontSize: 13, lineHeight: 1.5 }}>
              {m.content}
            </div>
          </div>
        ))}
        {loading && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <div style={{ width: 26, height: 26, borderRadius: 6, background: 'var(--nx-surface)', border: '1px solid var(--nx-border)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><NexusIcon name="Sparkles" size={12} style={{ color: 'var(--nx-accent)' }} /></div>
            <div style={{ padding: '8px 12px', borderRadius: '4px 12px 12px 12px', background: 'var(--nx-surface)', border: '1px solid var(--nx-border)', display: 'flex', gap: 4 }}>
              {[0,1,2].map(i => <div key={i} style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--nx-accent)', opacity: 0.7, animation: `pulse ${0.9 + i * 0.2}s ease-in-out infinite` }} />)}
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {messages.length === 0 && (
        <div style={{ padding: '0 16px 8px', display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {SUGGESTED.map((s, i) => (
            <button key={i} onClick={() => send(s)} style={{ padding: '5px 12px', borderRadius: 20, border: '1px solid var(--nx-border)', background: 'var(--nx-surface)', color: 'var(--nx-text)', fontSize: 12, cursor: 'pointer' }}>{s}</button>
          ))}
        </div>
      )}

      <div style={{ padding: '8px 12px', borderTop: '1px solid var(--nx-border)', display: 'flex', gap: 8 }}>
        <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && !e.shiftKey && send(input)} placeholder="Ask Copilot anything..." style={{ flex: 1, padding: '8px 12px', borderRadius: 8, border: '1px solid var(--nx-border)', background: 'var(--nx-bg)', color: 'var(--nx-text)', fontSize: 13 }} />
        <button onClick={() => send(input)} disabled={loading || !input.trim()} style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: loading || !input.trim() ? 'var(--nx-border)' : 'var(--nx-accent)', color: '#fff', cursor: loading || !input.trim() ? 'default' : 'pointer', fontWeight: 600 }}>
          <NexusIcon name="Send" size={14} />
        </button>
        {messages.length > 0 && <button onClick={() => setMessages([])} title="Clear chat" style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid var(--nx-border)', background: 'transparent', color: 'var(--nx-text-muted)', cursor: 'pointer' }}><NexusIcon name="Trash2" size={13} /></button>}
      </div>
    </div>
  );
};

window.LlmGatewaySettings = LlmGatewaySettings;
window.CopilotChat = CopilotChat;
